// ═══════════════════════════════════════════════════════════════
//  MOTEUR D'INDEXATION DES LOYERS — BELGIQUE
//  Régions : Wallonie, Bruxelles, Flandre
//  Base légale : indice santé (Statbel)
// ═══════════════════════════════════════════════════════════════

/**
 * Détermine la base d'indice à utiliser selon la date de signature.
 * Statbel a changé de base en janvier 2026 (2025=100).
 * On utilise toujours la base "vivante" au moment de la signature,
 * mais comme on stocke les 2 bases, on reste cohérent.
 *
 * Règle simple : on calcule dans la base 2013 pour tous les baux
 * (les 2 indices ref + courant lus dans la même colonne indice_2013).
 * La base 2025 est stockée en backup / affichage.
 */

const REGIONS = ['wallonie', 'bruxelles', 'flandre']

// ───────────────────────────────────────────────────────────────
//  FLANDRE — Facteur de correction PEB (durable, en vigueur 2026)
//  Source officielle : vlaanderen.be (huurprijsindexatie met correctiefactoren)
//  Conditions cumulatives :
//   1) Région = Flandre
//   2) Bail entré en vigueur AVANT le 01/10/2022
//   3) PEB = D, E, F, ou absent/expiré
//  Facteur selon le MOIS de la date anniversaire d'entrée en vigueur + label.
//  Colonne 'D' pour PEB D ; colonne 'EF' pour PEB E, F ou absent.
// ───────────────────────────────────────────────────────────────
const CORRECTION_FLANDRE = {
  1:  { D: 0.951950895, EF: 0.903901791 },  // janvier
  2:  { D: 0.961757813, EF: 0.923515625 },  // février
  3:  { D: 0.967996216, EF: 0.935992433 },  // mars
  4:  { D: 0.965766823, EF: 0.931533646 },  // avril
  5:  { D: 0.971941594, EF: 0.943883189 },  // mai
  6:  { D: 0.972124068, EF: 0.944248135 },  // juin
  7:  { D: 0.976119286, EF: 0.952238571 },  // juillet
  8:  { D: 0.977109655, EF: 0.954219311 },  // août
  9:  { D: 0.980049682, EF: 0.960099363 },  // septembre
  10: { D: 0.949447646, EF: 0.898895293 },  // octobre
  11: { D: 0.945356473, EF: 0.890712946 },  // novembre
  12: { D: 0.951977401, EF: 0.903954802 },  // décembre
}

// Date limite d'entrée en vigueur pour que la correction flamande s'applique
const FLANDRE_DATE_LIMITE = new Date('2022-10-01')

/**
 * Détermine le facteur de correction PEB flamand applicable.
 * @param {object} bail - { region, date_entree, peb }
 * @param {Date} dateAnniv - date anniversaire de l'indexation
 * @returns {{ facteur:number, applique:boolean, raison:string|null, colonne:string|null }}
 */
function facteurCorrectionFlandre(bail, dateAnniv) {
  const region = (bail.region || '').toLowerCase()
  if (region !== 'flandre') return { facteur: 1, applique: false, raison: null, colonne: null }

  // Condition 2 : bail entré en vigueur avant le 01/10/2022
  const dateEntree = bail.date_entree ? new Date(bail.date_entree) : null
  if (!dateEntree || dateEntree >= FLANDRE_DATE_LIMITE) {
    return { facteur: 1, applique: false, raison: null, colonne: null }
  }

  // Condition 3 : PEB D, E, F ou absent
  const peb = (bail.peb || '').toUpperCase().trim()
  const sansPeb = peb === '' || peb === 'AUCUN'
  let colonne = null
  if (peb === 'D') colonne = 'D'
  else if (['E', 'F', 'G'].includes(peb) || sansPeb) colonne = 'EF'
  else {
    // PEB A, A+, B, C → indexation pleine, pas de correction
    return { facteur: 1, applique: false, raison: null, colonne: null }
  }

  const mois = dateAnniv.getMonth() + 1  // 1-12
  const ligne = CORRECTION_FLANDRE[mois]
  if (!ligne) return { facteur: 1, applique: false, raison: null, colonne: null }

  const facteur = ligne[colonne]
  const labelTxt = colonne === 'D' ? 'PEB D' : (sansPeb ? 'sans PEB' : `PEB ${peb}`)
  return {
    facteur,
    applique: true,
    colonne,
    raison: `Logement flamand ${labelTxt}, bail antérieur au 01/10/2022 : un facteur de correction légal de ${facteur.toFixed(4)} s'applique à l'indexation.`,
  }
}

/**
 * Récupère l'indice santé d'un mois donné dans la base voulue.
 * @param {Array} indices - lignes de la table indices_sante
 * @param {number} annee
 * @param {number} mois (1-12)
 * @param {string} baseCol - 'indice_2013' ou 'indice_2025'
 * @returns {number|null}
 */
function getIndice(indices, annee, mois, baseCol = 'indice_2013') {
  const row = indices.find(i => i.annee === annee && i.mois === mois)
  if (!row) return null
  return row[baseCol] != null ? Number(row[baseCol]) : null
}

/**
 * Détermine le mois/année de l'indice de DÉPART selon la région.
 * - Wallonie / Bruxelles : mois précédant la SIGNATURE du bail
 * - Flandre (contrats post 1/1/2019) : mois précédant l'ENTRÉE EN VIGUEUR
 *
 * @param {object} bail - { date_signature, date_entree, region }
 * @returns {{annee:number, mois:number}}
 */
function moisIndiceDepart(bail) {
  const region = (bail.region || 'wallonie').toLowerCase()
  // Date de base selon région
  const dateRef = (region === 'flandre' && bail.date_entree)
    ? new Date(bail.date_entree)
    : new Date(bail.date_signature || bail.date_entree)

  // Mois PRÉCÉDENT
  let annee = dateRef.getFullYear()
  let mois = dateRef.getMonth() // getMonth() = 0-11, donc déjà "mois - 1" en base 1-12
  if (mois === 0) { mois = 12; annee -= 1 }
  return { annee, mois }
}

/**
 * Détermine le mois/année du NOUVEL indice (date anniversaire).
 * Mois précédant la date anniversaire de l'entrée en vigueur.
 *
 * @param {object} bail - { date_entree }
 * @param {Date} dateAnniv - la date anniversaire considérée (cette année)
 * @returns {{annee:number, mois:number}}
 */
function moisNouvelIndice(dateAnniv) {
  let annee = dateAnniv.getFullYear()
  let mois = dateAnniv.getMonth() // déjà mois précédent (0-11 -> 1-12 du mois d'avant)
  if (mois === 0) { mois = 12; annee -= 1 }
  return { annee, mois }
}

/**
 * Calcule l'indexation d'un loyer belge.
 *
 * @param {object} params
 * @param {object} params.bail - {
 *     loyer_initial, date_signature, date_entree, region, peb,
 *     indice_reference (optionnel, sinon lu dans table)
 *   }
 * @param {Array} params.indices - table indices_sante
 * @param {Date} params.dateAnniv - date anniversaire à indexer (défaut: prochaine)
 * @param {string} params.baseCol - base d'indice ('indice_2013')
 * @returns {object} résultat détaillé
 */
function calculerIndexation({ bail, indices, dateAnniv, baseCol = 'indice_2013' }) {
  const result = {
    ok: false,
    loyer_initial: Number(bail.loyer_initial),
    nouveau_loyer: null,
    indice_depart: null,
    indice_nouveau: null,
    mois_depart: null,
    mois_nouveau: null,
    augmentation: null,
    pourcentage: null,
    loyer_plein: null,          // loyer après formule standard, avant correction PEB
    correction_appliquee: false,
    correction_facteur: null,
    correction_label: null,
    avertissements: [],
    erreur: null,
  }

  const region = (bail.region || 'wallonie').toLowerCase()
  if (!REGIONS.includes(region)) {
    result.erreur = `Région inconnue: ${region}`
    return result
  }
  if (!bail.loyer_initial || Number(bail.loyer_initial) <= 0) {
    result.erreur = 'Loyer initial manquant ou invalide'
    return result
  }

  // 1) Indice de départ
  const mDepart = moisIndiceDepart(bail)
  result.mois_depart = mDepart
  let indiceDepart = bail.indice_reference != null
    ? Number(bail.indice_reference)
    : getIndice(indices, mDepart.annee, mDepart.mois, baseCol)

  if (indiceDepart == null) {
    result.erreur = `Indice de départ introuvable pour ${mDepart.annee}-${String(mDepart.mois).padStart(2,'0')}`
    return result
  }
  result.indice_depart = indiceDepart

  // 2) Nouvel indice (date anniversaire)
  const mNouveau = moisNouvelIndice(dateAnniv)
  result.mois_nouveau = mNouveau
  const indiceNouveau = getIndice(indices, mNouveau.annee, mNouveau.mois, baseCol)

  if (indiceNouveau == null) {
    result.erreur = `Nouvel indice introuvable pour ${mNouveau.annee}-${String(mNouveau.mois).padStart(2,'0')}`
    return result
  }
  result.indice_nouveau = indiceNouveau

  // 3) Formule légale : Loyer base × (Nouvel indice / Indice départ)
  const loyerPlein = Number(bail.loyer_initial) * (indiceNouveau / indiceDepart)
  result.loyer_plein = Math.round(loyerPlein * 100) / 100

  // 3bis) Facteur de correction PEB (Flandre uniquement, baux < 01/10/2022, PEB D/E/F/absent)
  const correction = facteurCorrectionFlandre(bail, dateAnniv)
  const loyerFinal = loyerPlein * correction.facteur

  result.nouveau_loyer = Math.round(loyerFinal * 100) / 100
  result.augmentation = Math.round((result.nouveau_loyer - result.loyer_initial) * 100) / 100
  result.pourcentage = Math.round(((result.nouveau_loyer / result.loyer_initial - 1) * 100) * 100) / 100

  if (correction.applique) {
    result.correction_appliquee = true
    result.correction_facteur = correction.facteur
    result.correction_label = correction.colonne === 'D' ? 'PEB D' : 'PEB E/F/sans PEB'
    result.avertissements.push(correction.raison)
  }

  // 4) Cas indexation négative (indice en baisse)
  if (indiceNouveau < indiceDepart) {
    result.avertissements.push(
      "L'indice a baissé : l'indexation est négative. Le loyer ne peut être augmenté ; " +
      "le locataire peut demander une diminution."
    )
  }

  // 5) Avertissement Flandre : PEB manquant (le calcul applique déjà la correction,
  //    mais on signale que renseigner le PEB fiabilise le résultat)
  if (region === 'flandre') {
    const peb = (bail.peb || '').toUpperCase().trim()
    const dateEntree = bail.date_entree ? new Date(bail.date_entree) : null
    const bailAncien = dateEntree && dateEntree < FLANDRE_DATE_LIMITE
    if (!peb && bailAncien) {
      result.avertissements.push(
        "Bien en Flandre sans label PEB pour un bail antérieur au 01/10/2022 : " +
        "le facteur de correction 'sans PEB' (le plus restrictif) a été appliqué. " +
        "Renseignez le PEB réel pour un calcul exact."
      )
    }
  }

  result.ok = true
  return result
}

/**
 * Détermine si un bail est "dû" pour indexation (date anniversaire passée
 * ou proche) et n'a pas encore été indexé cette année.
 *
 * @param {object} bail - { date_entree, date_indexation (dernière) }
 * @param {Date} today
 * @param {number} fenetreJours - nombre de jours avant l'anniversaire pour notifier
 * @returns {{du:boolean, dateAnniv:Date|null, joursRestants:number|null}}
 */
function estDuPourIndexation(bail, today = new Date(), fenetreJours = 30) {
  if (!bail.date_entree) return { du: false, dateAnniv: null, joursRestants: null }

  const entree = new Date(bail.date_entree)
  const anneeActuelle = today.getFullYear()

  // Date anniversaire cette année
  let dateAnniv = new Date(anneeActuelle, entree.getMonth(), entree.getDate())
  // Si déjà passée cette année de plus de 6 mois, viser l'an prochain n'a pas de sens
  // pour la notification ; on garde l'anniversaire de cette année.

  // Le bail doit avoir au moins 1 an : la date anniversaire considérée
  // doit être postérieure à (entrée + 1 an)
  const premierAnniv = new Date(entree.getFullYear() + 1, entree.getMonth(), entree.getDate())
  if (dateAnniv < premierAnniv) {
    // L'anniversaire de cette année est avant le 1er anniversaire réel
    // => bail trop récent, pas encore indexable
    return { du: false, dateAnniv, joursRestants: null }
  }

  // A-t-on déjà indexé pour cet anniversaire ?
  if (bail.date_indexation) {
    const dernIdx = new Date(bail.date_indexation)
    if (dernIdx.getFullYear() === anneeActuelle &&
        dernIdx >= new Date(anneeActuelle, 0, 1)) {
      return { du: false, dateAnniv, joursRestants: null }
    }
  }

  const diffMs = dateAnniv.getTime() - today.getTime()
  const joursRestants = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  // Dû si l'anniversaire est dans la fenêtre (à venir) OU déjà passé cette année
  const du = joursRestants <= fenetreJours

  return { du, dateAnniv, joursRestants }
}

// Export (selon votre système de modules)
export {
  REGIONS,
  calculerIndexation,
  estDuPourIndexation,
  getIndice,
  moisIndiceDepart,
  moisNouvelIndice,
}